export default function Cadastro() {
  const { slug } = useParams<{ slug: string }>();
  const [form, setForm] = useState({
    name: '',
    cpf: '',
    email: '',
    cellphone: '',
    password: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const formatCPF = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: name === 'cpf' ? formatCPF(value) : name === 'cellphone' ? formatPhone(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('/api/cadastro', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ ...form, slug }),
      });

      const data = await res.json();

      if (res.status === 201) {
        setStatus('success');
      } else {
        const msg = data.errors
          ? Object.values(data.errors).flat().join(' ')
          : data.message ?? 'Erro ao realizar cadastro.';
        setErrorMsg(msg as string);
        setStatus('error');
      }
    } catch {
      setErrorMsg('Erro de conexão. Tente novamente.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1628] via-[#0F1B35] to-[#1A0B2E] flex items-center justify-center px-6">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-[#00A8E8] rounded-full opacity-10 blur-3xl" />
          <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-[#E91E63] rounded-full opacity-10 blur-3xl" />
        </div>

        <div className="relative z-10 w-full max-w-md text-center">
          <div className="mb-6 flex items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00A8E8] to-[#E91E63] flex items-center justify-center shadow-2xl shadow-[#00A8E8]/40">
              <i className="ri-check-line text-4xl text-white"></i>
            </div>
          </div>

          <h2 className="text-4xl font-bold text-white mb-3">Cadastro realizado!</h2>
          <p className="text-gray-300 mb-10">
            Bem-vindo ao VitaUni. Agora baixe o app para ativar seu plano e começar a economizar em saúde.
          </p>

          <div className="space-y-4 mb-6">
            <a
              href="https://play.google.com/store/apps/details?id=br.com.vitauni&hl=pt_BR"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-white text-gray-900 rounded-full hover:bg-gray-100 transition-all shadow-xl cursor-pointer group"
            >
              <i className="ri-google-play-fill text-2xl text-[#00A8E8]"></i>
              <div className="text-left">
                <div className="text-xs text-gray-500">Disponível na</div>
                <div className="font-bold">Google Play</div>
              </div>
              <i className="ri-arrow-right-line ml-auto group-hover:translate-x-1 transition-transform"></i>
            </a>

            <a
              href="https://apps.apple.com/br/app/vitauni/id6757611955"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-white text-gray-900 rounded-full hover:bg-gray-100 transition-all shadow-xl cursor-pointer group"
            >
              <i className="ri-apple-fill text-2xl"></i>
              <div className="text-left">
                <div className="text-xs text-gray-500">Disponível na</div>
                <div className="font-bold">App Store</div>
              </div>
              <i className="ri-arrow-right-line ml-auto group-hover:translate-x-1 transition-transform"></i>
            </a>

            <a
              href="https://clube.vitauni.com.br/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full px-6 py-3 border border-white/30 text-white/70 rounded-full hover:border-white/60 hover:text-white transition-all text-sm cursor-pointer"
            >
              <i className="ri-global-line"></i>
              Continuar pela web
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1628] via-[#0F1B35] to-[#1A0B2E] flex items-center justify-center px-6 py-12">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#00A8E8] rounded-full opacity-10 blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-[#E91E63] rounded-full opacity-10 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/">
            <img src="/img/logo.png" alt="VitaUni" className="h-12 w-auto mx-auto mb-4" />
          </a>
          {slug && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm">
              <i className="ri-links-line text-[#00A8E8] text-sm"></i>
              <span className="text-sm text-white/80">Indicação de <span className="font-semibold text-white">{slug}</span></span>
            </div>
          )}
        </div>

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-8">
          <h1 className="text-2xl font-bold text-white mb-1">Crie sua conta</h1>
          <p className="text-gray-400 text-sm mb-8">Saúde acessível para você e sua família.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Nome completo</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Maria Silva Santos"
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#00A8E8] focus:ring-1 focus:ring-[#00A8E8] transition-all"
              />
            </div>

            {/* CPF */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">CPF</label>
              <input
                type="text"
                name="cpf"
                value={form.cpf}
                onChange={handleChange}
                placeholder="000.000.000-00"
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#00A8E8] focus:ring-1 focus:ring-[#00A8E8] transition-all"
              />
            </div>

            {/* E-mail */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">E-mail</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="seu@email.com"
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#00A8E8] focus:ring-1 focus:ring-[#00A8E8] transition-all"
              />
            </div>

            {/* Celular */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Celular (WhatsApp)</label>
              <input
                type="tel"
                name="cellphone"
                value={form.cellphone}
                onChange={handleChange}
                placeholder="(55) 99999-9999"
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#00A8E8] focus:ring-1 focus:ring-[#00A8E8] transition-all"
              />
            </div>

            {/* Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Senha</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                  className="w-full px-4 py-3 pr-12 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#00A8E8] focus:ring-1 focus:ring-[#00A8E8] transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(p => !p)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            {/* Erro */}
            {status === 'error' && (
              <div className="flex items-start gap-2 px-4 py-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-300 text-sm">
                <i className="ri-error-warning-line mt-0.5 shrink-0"></i>
                <span>{errorMsg}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full py-4 bg-gradient-to-r from-[#00A8E8] to-[#E91E63] text-white font-semibold rounded-xl hover:shadow-2xl hover:shadow-[#00A8E8]/30 transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
            >
              {status === 'loading' ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-lg"></i>
                  Cadastrando...
                </>
              ) : (
                <>
                  Criar conta grátis
                  <i className="ri-arrow-right-line text-lg"></i>
                </>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-gray-500 mt-6">
            Ao se cadastrar você aceita nossos{' '}
            <a href="#" className="text-[#00A8E8] hover:underline">Termos de Uso</a>{' '}
            e{' '}
            <a href="#" className="text-[#00A8E8] hover:underline">Política de Privacidade</a>.
          </p>
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          Já tem conta?{' '}
          <a href="https://clube.vitauni.com.br/" target="_blank" rel="noopener noreferrer" className="text-[#00A8E8] hover:underline">
            Acesse pela web
          </a>
        </p>
      </div>
    </div>
  );
}
